import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import * as PropTypes from 'prop-types';
import styles from './styles.modules.scss';
import {DataContext, ScrollsContext} from 'contexts';
import Popover from 'ati-ui-react/components/Tip/Popover';
import Link from 'ati-ui-react/components/Link';
import InputField from 'ati-ui-react/components/InputField';
import PlainText from 'ati-ui-react/components/PlainText';
import CheckThin from 'ati-ui-react/components/icons/CheckThin';
import PlusSolid from 'ati-ui-react/components/icons/PlusSolid';
import ScrollRow from './ScrollRow/ScrollRow';
import {ScrollsPopupStages} from 'components/Page/SearchResults/ScrollsPopoverContainer/consts';
import {useLocalized} from 'utils/useLocalized';
import i18nStrings from './strings';
import Button from 'ati-ui-react/components/Button/v3';
import {classes} from 'as-ui-kit/utils/styles';

const ScrollsPopover = ({atiIds, visible, scrollsPopupVisible, children, onOpenScrollsPopup, onPopoverVisibleChange}) =>{
    const {isMobile} =  useContext(DataContext);
    const {scrolls, updateScrolls, showScrollsSaved} = useContext(ScrollsContext);

    const strings = useLocalized(i18nStrings);

    const [searchValue, setSearchValue] = useState('');
    const [selectedScrolls, setSelectedScrolls] = useState({});

    const onScrollToggle = useCallback((scroll) => {
        setSelectedScrolls((prevState) => ({
	        ...prevState,
	        [scroll.id]: {
                ...prevState[scroll.id],
                checked: !prevState[scroll.id].checked,
	        },
        }));
    }, []);

    const onSaveClick = useCallback(()=> {
        const allowedSelectedScrolls = Object.entries(selectedScrolls).filter(([,s]) => s.checked && !s.isCheckedUsersAddedToScroll);
	    //дефолтные(как и любые другие) списки нужно создавать самим. По умолчанию их не существует, но отображать на фронте надо
	    const newDefaultScrolls = allowedSelectedScrolls.filter(([, scroll])=> scroll.default).map(([, scroll])=> scroll);

        const serverObj = [];
        for (const [listId] of allowedSelectedScrolls){
            for (const atiId of atiIds) {
	            serverObj.push({
		            ati_id: atiId,
		            list_id: listId
	            });
            }
        }
        updateScrolls(serverObj, newDefaultScrolls);
    }, [atiIds, selectedScrolls, updateScrolls]);

    useEffect(() => {
        if (visible && scrolls) {
	        const selectedScrollIds = {};

	        scrolls.forEach(scroll => {
		        const elementIdsSet = new Set(scroll.elements.map(e => e.atiId));
		        const isChecked = atiIds.every(id => elementIdsSet.has(id));
		        selectedScrollIds[scroll.id] = {
                    ...scroll,
                    checked: isChecked,
			        isCheckedUsersAddedToScroll: isChecked,
		        };
	        });

	        setSelectedScrolls(selectedScrollIds);
        }
    }, [atiIds, scrolls, visible]);

    return (
        <>
            <Popover
                isAdaptive
                restorePosition
                control={(
                    <div className={styles['popup-footer']}>
                        <Button
	                        disabled={!Object.values(selectedScrolls).some(s => s.checked && !s.isCheckedUsersAddedToScroll)}
                            mobile={true}
                            size="small"
                            onClick={onSaveClick}
                        >
                            {strings.save}
                        </Button>
                        {showScrollsSaved && (
                            <PlainText
                                secondary
                                className={styles['saved']}
                                size="small"
                            >
                                <CheckThin
                                    color="#64B82A"
                                    height="10px"
                                />
                                {strings.saved}
                            </PlainText>
                        )}
                    </div>
                )}
                fixedBodyPosition={isMobile}
                isCenterAlignmentOnAdaptive={false}

                isClosedOnOutsideClick={!isMobile && !scrollsPopupVisible}
                mainContent={(

                    <React.Fragment>
                        <div
                            className={styles['popover-header']}
                        >
                            <span>
                                {strings.chooseLists}
                            </span>
                        </div>
                        <div className={styles['popover-description']} >
                            <span>
                                {strings.listDescription}
                            </span>
                        </div>
                        <div className={styles['main-content']}>
                            <div className={styles['search']}>
                                <InputField
                                    placeholder={strings.searchPlaceholder}
                                    value={searchValue}
                                    onChange={event => setSearchValue(event.target.value)}
                                />
                            </div>
                            <div className={styles['scrolls-block']}>
                                {scrolls?.filter(s=>s.name.toLowerCase().includes(searchValue.toLowerCase())).map(l=> {
	                                return (
		                                <ScrollRow
			                                atiIds={atiIds}
			                                key={l.id}
			                                scroll={l}
			                                selectedScroll={selectedScrolls[l.id]}
			                                onOpenScrollsPopup={onOpenScrollsPopup}
			                                onScrollToggle={onScrollToggle}
		                                />
	                                );
                                })}
                            </div>
                        </div>
                        <div className={styles['footer-content']}>
                            <Link
                                before={(
                                    <PlusSolid
                                        height="16"
                                        width="16"
                                    />
                                )}
                                onClick={()=>onOpenScrollsPopup(ScrollsPopupStages.create, null)}
                            >
                                <span>
                                    {strings.createList}
                                </span>
                            </Link>
                        </div>
                        {' '}
                    </React.Fragment>
                )}
                mainContentClassName={styles['content-section']}
                placement="top"
                templateWrapperClassName={classes(styles['popover-container'], isMobile && styles['mobile'])}
                visible={visible}

                onCloseClick={(event)=> {
	                setSearchValue('');
	                onPopoverVisibleChange(false, event);
                }}
                onVisibilityChange={onPopoverVisibleChange}
            >
                <span
                    onClick={() => {
                        onPopoverVisibleChange(true);
                    }}
                >
                    {children}
                </span>
            </Popover>
        </>
    );
};
ScrollsPopover.defaultProps = {
    visible: false
};

export default ScrollsPopover;

ScrollsPopover.propTypes ={
    atiIds: PropTypes.string,
    children: PropTypes.node,
    scrollsPopupVisible: PropTypes.bool,
    visible: PropTypes.bool,
    onOpenScrollsPopup: PropTypes.func,
    onPopoverVisibleChange: PropTypes.func
};

